import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Trash2, RefreshCw, Calculator } from "lucide-react";

// Delete Confirmation Dialog
export const DeleteProductDialog = ({
  open,
  onOpenChange,
  productToDelete,
  deleting,
  onConfirmDelete,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            Delete Product
          </DialogTitle>
        </DialogHeader>
        
        {productToDelete && (
          <div className="py-4">
            <p className="text-gray-700 mb-4">
              Are you sure you want to permanently delete this product?
            </p>
            
            <div className="bg-gray-100 p-4 rounded-lg">
              <p className="font-semibold text-lg">{productToDelete.name}</p>
              <div className="text-sm text-gray-600 mt-2 space-y-1">
                <p>PLU Code: {productToDelete.plu_code || 'N/A'}</p>
                <p>Barcode: {productToDelete.barcode || 'N/A'}</p>
                <p>Category: {productToDelete.category || 'N/A'}</p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> This action cannot be undone. All product data including sales history references will be affected.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={onConfirmDelete}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {deleting ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Product
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Formula Settings Dialog
export const FormulaSettingsDialog = ({
  open,
  onOpenChange,
  formulaSettings,
  setFormulaSettings,
  onSave,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Stock Level Formula Settings
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="bg-blue-50 p-4 rounded-lg text-sm">
            <p className="font-medium text-blue-800 mb-2">Formula Explanation:</p>
            <ul className="text-blue-700 space-y-1">
              <li>• Uses 6 months of sales data</li>
              <li>• Weekly Sales = (6-month total ÷ 6) ÷ 4</li>
              <li>• Each level = Weekly Sales × Multiplier</li>
            </ul>
          </div>

          <div>
            <Label>Minimum Level Multiplier</Label>
            <Input
              type="number"
              step="0.5"
              value={formulaSettings.minimum_level_multiplier}
              onChange={(e) => setFormulaSettings(prev => ({
                ...prev,
                minimum_level_multiplier: parseFloat(e.target.value) || 0
              }))}
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum = Weekly Sales × {formulaSettings.minimum_level_multiplier}
            </p>
          </div>

          <div>
            <Label>Reorder Level Multiplier</Label>
            <Input
              type="number"
              step="0.5"
              value={formulaSettings.reorder_level_multiplier}
              onChange={(e) => setFormulaSettings(prev => ({
                ...prev,
                reorder_level_multiplier: parseFloat(e.target.value) || 0
              }))}
            />
            <p className="text-xs text-gray-500 mt-1">
              Reorder = Weekly Sales × {formulaSettings.reorder_level_multiplier}
            </p>
          </div>

          <div>
            <Label>Maximum Level Multiplier</Label>
            <Input
              type="number"
              step="0.5"
              value={formulaSettings.maximum_level_multiplier}
              onChange={(e) => setFormulaSettings(prev => ({
                ...prev,
                maximum_level_multiplier: parseFloat(e.target.value) || 0
              }))}
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum = Weekly Sales × {formulaSettings.maximum_level_multiplier}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} className="bg-purple-600 hover:bg-purple-700">
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
